// External
import { useCallback } from 'react';
import { useStoreActions, useStoreState } from '@/store';

// Internal
import { queryCheckoutSession } from '@/lib/graphql';
import { getToken } from '@/lib/globals';
import { features } from '@/lib/features';
import { logger } from '@/lib/logger';
import { noop } from '@/lib/utils/miscUtils';
import {
  getShippingAddress,
  getShippingCountry,
  mapAddressValues,
} from '@/lib/utils/shipping';
import { SHIPPING_CALLBACK_ERROR_MAPPING } from '@/lib/utils/shipping/constants';
import { logonShippingAddressChangeReceivedToFPTI } from '@/lib/utils/shipping/fpti';
import { useShippingAddressChangeCallback } from './useShippingAddressChangeCallback';
import { conditionallyFetchConversionData } from '@/lib/graphql/operations/currencyConversion';

/**
 * @NOTE
 * This is the hook that will be utilized by other components such as `Address`.
 *
 * {@link https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=853380692}
 */
export const useShippingAddressChange = () => {
  const cartId = getToken();
  const cartDetails = useStoreState(({ api }) => api.cartDetails);
  const shippingMethods = useStoreState(({ api }) => api.shippingMethods);

  const setCartData = useStoreActions(({ api }) => api.setCartData);
  const setPageLevelError = useStoreActions(({ ui }) => ui.setPageLevelError);
  const setShowLoadingSpinnerWithMessage = useStoreActions(
    ({ ui }) => ui.setShowLoadingSpinnerWithMessage,
  );
  const isShippingCallbacksV2Eligible = features.isActive(
    'isShippingCallbacksV2Eligible',
  );
  const { amounts } = cartDetails;

  const onShippingAddressChange = useShippingAddressChangeCallback(
    cartId,
    shippingMethods,
    amounts,
  );

  const execute = useCallback(async () => {
    try {
      const shippingAddress = {
        ...getShippingAddress(),
        country: { value: getShippingCountry() },
      };
      const formattedShippingAddress = mapAddressValues(shippingAddress);
      // Fire onShippingAddressChange callback
      const didCallbackFire = await onShippingAddressChange(
        formattedShippingAddress,
        setShowLoadingSpinnerWithMessage,
      );

      if (didCallbackFire) {
        // The checkoutSession and currency conversion queries
        // do NOT depend on each other; hence, invoking them
        // in parallel is fine.
        const [checkoutSessionData] = await Promise.all([
          queryCheckoutSession(),
          conditionallyFetchConversionData(null, true),
        ]);
        const { cart } = checkoutSessionData ?? {};
        const { amounts, items, shippingMethods = [] } = cart ?? {};

        setCartData({
          cartAmount: amounts?.total,
          cartDetails: {
            ...cartDetails,
            amounts,
            items,
          },
          shippingMethods: shippingMethods,
        });

        setPageLevelError(''); // Clear error on success
        setShowLoadingSpinnerWithMessage(null);

        logonShippingAddressChangeReceivedToFPTI({ status: 'success' });
        logger.info('on_shipping_address_change_callback_is_called');
      }
    } catch (err) {
      setShowLoadingSpinnerWithMessage(null);
      const errMsg = typeof err === 'string' ? err : err?.message;
      const errorLookup = SHIPPING_CALLBACK_ERROR_MAPPING[errMsg];
      if (errorLookup) {
        logonShippingAddressChangeReceivedToFPTI({
          errorDescription: errorLookup,
          status: 'failure',
        });
        setPageLevelError(errorLookup);
        logger.error('on_shipping_address_change_callback_known_error', {
          error_description: errorLookup,
        });
      } else {
        logonShippingAddressChangeReceivedToFPTI({
          errorDescription: SHIPPING_CALLBACK_ERROR_MAPPING.DEFAULT,
          status: 'failure',
        });
        // Handle generic error
        setPageLevelError(SHIPPING_CALLBACK_ERROR_MAPPING.DEFAULT);
        logger.error('on_shipping_address_change_callback_generic_error', {
          error_description: errMsg,
        });
      }
      window.scrollTo(0, 0);
    }
  }, [onShippingAddressChange, shippingMethods]);

  if (!isShippingCallbacksV2Eligible) {
    return noop;
  }

  return execute;
};
